import React, { MouseEvent, useState } from 'react'
import {
  Button,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Grid,
  Typography,
  Paper,
} from '@material-ui/core'
import { X } from 'lib/lucide'
import { DatePicker } from '@material-ui/pickers'
import { Moment } from 'moment'

import { ERRORS, STRINGS } from 'consts'
import { useApi, useApiCall } from 'hooks'
import { snackbar } from 'components'

import {
  downloadCSV,
  formatUserCSV,
  formatPrizesCSV,
  formatCSV,
  formatUserTestCSV,
  formatCouponCSV,
  formatInvoicesCSV,
} from './utils/parseCsv'
import {
  downloadUsers,
  downloadUserPrizes,
  downloadUserTest,
  downloadUserCoupons,
  downloadInvoices,
} from './api'
import {
  DownloadParams,
  UserParams,
  UserTestParams,
  GetUserTestResponse,
  GetInvoicesResponse,
} from './types'
import {
  GetUsersResponse,
  GetPrizesUsersResponse,
  GetUsersVisitsResponse,
  GetCouponsUsersResponse,
} from '../Users/types'
import { GetStoresResponse, Store } from '../Stores/types'
import { getStores } from '../Stores/api'
import { useStyles } from './styles'

type DataType =
  | 'Usuarios'
  | 'UsuariosVisita'
  | 'Cupones'
  | 'Premios'
  | 'Reward'
  | 'UserTest'
  | 'Facturas'

const Download: React.FC = () => {
  const classes = useStyles()
  const [startDate, setStartDate] = useState<Moment | null>(null)
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [dataType, setDataType] = useState<DataType>('Usuarios')
  const [userSelected, setUserSelected] = useState<number>(0)
  const [storeSelected, setStoreSelected] = useState<number>(0)

  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []

  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    {
      baseData: [],
      onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
    },
  )

  const [requestUsers, isRequestingUsers] = useApiCall<UserParams, GetUsersResponse>(downloadUsers)
  const [requestUsersVisit, isRequestingUsersVisit] = useApiCall<
    UserParams,
    GetUsersVisitsResponse
  >(downloadUsers)

  const [requestUserCoupons] = useApiCall<
    DownloadParams,
    GetCouponsUsersResponse
  >(downloadUserCoupons)

  const [requestUserPrizes, isRequestingUserPrizes] = useApiCall<
    DownloadParams,
    GetPrizesUsersResponse
  >(downloadUserPrizes)

  const [requestUserTest, isRequestingUserTest] = useApiCall<UserTestParams, GetUserTestResponse>(
    downloadUserTest,
  )

  const [requestInvoices, isRequestingInvoices] = useApiCall<DownloadParams, GetInvoicesResponse>(
    downloadInvoices,
  )

  const handleStartDateChange = (newDate: Moment | null) => {
    if (newDate) setStartDate(newDate.startOf('day'))
  }

  const handleEndDateChange = (newDate: Moment | null) => {
    if (newDate) setEndDate(newDate.endOf('day'))
  }

  const handleDataSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setDataType(event.target.value as DataType)
  }

  const handleUserSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setUserSelected(event.target.value as number)
  }

  const handleStoreSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setStoreSelected(event.target.value as number)
  }

  const handleClearStart = (e: MouseEvent) => {
    e.stopPropagation()
    setStartDate(null)
  }

  const handleClearEnd = (e: MouseEvent) => {
    e.stopPropagation()
    setEndDate(null)
  }

  const handleDownload = async () => {
    if (!startDate || !endDate) {
      return snackbar.show('Las fechas son obligatorias')
    }

    if (dataType === 'Usuarios') {
      const { users: usersToCSV } = await requestUsers({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        download: true,
      })
      if (usersToCSV === null) {
        return snackbar.show(ERRORS.LIMIT_ROWS_ERROR_MESSAGE)
      }
      if (usersToCSV.length) return downloadCSV(formatCSV(usersToCSV), dataType)
      return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }

    if (dataType === 'UsuariosVisita') {
      const { users: usersToCSV } = await requestUsersVisit({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        downloadVisit: true,
      })
      if (usersToCSV === null) {
        return snackbar.show(ERRORS.LIMIT_ROWS_ERROR_MESSAGE)
      }
      if (usersToCSV.length) return downloadCSV(formatUserCSV(usersToCSV), dataType)
      return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }

    if (dataType === 'Cupones') {
      const { coupons: couponsToCSV } = await requestUserCoupons({
        id: userSelected,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        storeId: storeSelected,
      })
      if (couponsToCSV.length) return downloadCSV(formatCouponCSV(couponsToCSV), 'Promo')
      return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }

    if (dataType === 'Premios' || dataType === 'Reward') {
      const { prizes: prizesToCSV } = await requestUserPrizes({
        id: userSelected,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        storeId: storeSelected,
        rewardsOnly: dataType === 'Reward',
      })
      if (prizesToCSV.length) return downloadCSV(formatPrizesCSV(prizesToCSV), dataType)
      return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }

    if (dataType === 'UserTest') {
      try {
        const { tests: testsToCSV } = await requestUserTest({
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        })
        if (testsToCSV === null) {
          return snackbar.show(ERRORS.LIMIT_ROWS_ERROR_MESSAGE)
        }
        if (testsToCSV.length) return downloadCSV(formatUserTestCSV(testsToCSV), dataType)
        return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      } catch (error) {
        return snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE)
      }
    }

    if (dataType === 'Facturas') {
      const { invoices: invoicesToCSV } = await requestInvoices({
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        storeId: storeSelected,
      })
      if (invoicesToCSV === null) {
        return snackbar.show(ERRORS.LIMIT_ROWS_ERROR_MESSAGE)
      }
      if (invoicesToCSV.length) return downloadCSV(formatInvoicesCSV(invoicesToCSV), dataType)
      return snackbar.show(ERRORS.NO_DATA_ERROR_MESSAGE)
    }

    return false
  }

  const isLoading =
    isRequestingUsers ||
    isRequestingUsersVisit ||
    isRequestingUserPrizes ||
    isRequestingUserTest ||
    isRequestingInvoices

  const isDownloadDisabled = isLoading || !startDate || !endDate

  return (
    <Paper className={classes.pageCard} elevation={2}>
      <Typography variant="h6" className={classes.pageTitle}>
        Descargas
      </Typography>
      <div className={classes.pageBody}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <Typography className={classes.fieldLabel}>Tipo de descarga</Typography>
            <Select value={dataType} onChange={handleDataSelect} fullWidth>
              <MenuItem value="Usuarios">Usuarios Registro</MenuItem>
              <MenuItem value="Cupones">Promos</MenuItem>
              <MenuItem value="Premios">Premios</MenuItem>
              <MenuItem value="Reward">Beneficios</MenuItem>
              <MenuItem value="UserTest">User Test</MenuItem>
              <MenuItem value="Facturas">Facturas</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={4} />
          <Grid item xs={12} sm={4}>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              autoOk
              orientation="landscape"
              format={STRINGS.DATE_FORMAT}
              initialFocusedDate={startDate}
              style={{ width: '100%' }}
              label="Desde *"
              required
              InputProps={{
                endAdornment: (
                  <IconButton onClick={(e: MouseEvent) => handleClearStart(e)}>
                    <X size={18} />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              autoOk
              orientation="landscape"
              format={STRINGS.DATE_FORMAT}
              initialFocusedDate={endDate}
              style={{ width: '100%' }}
              label="Hasta *"
              required
              InputProps={{
                endAdornment: (
                  <IconButton onClick={e => handleClearEnd(e)}>
                    <X size={18} />
                  </IconButton>
                ),
              }}
            />
          </Grid>

          {(dataType === 'Premios' || dataType === 'Reward' || dataType === 'Facturas') && (
            <Grid item xs={12} sm={8}>
              <Typography className={classes.fieldLabel}>Usuario</Typography>
              <Select
                value={userSelected}
                onChange={handleUserSelect}
                fullWidth
                placeholder="Seleccionar usuario"
              >
                <MenuItem value={0}>Todos los usuarios</MenuItem>
              </Select>
              <Typography className={classes.fieldLabel}>Tienda</Typography>
              <Select
                value={storeSelected}
                onChange={handleStoreSelect}
                fullWidth
                defaultValue={0}
                placeholder="Seleccionar tienda"
              >
                <MenuItem value={0}>Todas las tiendas</MenuItem>
                {stores.map(store => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.id} - {store.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}

          {dataType === 'Cupones' && (
            <Grid item xs={12} sm={8}>
              <Typography className={classes.fieldLabel}>Usuario</Typography>
              <Select
                value={userSelected}
                onChange={handleUserSelect}
                fullWidth
                defaultValue={0}
                placeholder="Seleccionar usuario"
              >
                <MenuItem value={0}>Todos los usuarios</MenuItem>
              </Select>
              <Typography className={classes.fieldLabel}>Tienda</Typography>
              <Select
                value={storeSelected}
                onChange={handleStoreSelect}
                fullWidth
                defaultValue={0}
                placeholder="Seleccionar tienda"
              >
                <MenuItem value={0}>Todas las tiendas</MenuItem>
                {stores.map(store => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.id} - {store.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
        </Grid>
      </div>
      <div className={classes.pageActions}>
        <Button
          onClick={handleDownload}
          variant="contained"
          color="primary"
          disabled={isDownloadDisabled}
        >
          Descargar
          {isLoading && (
            <CircularProgress style={{ color: 'gray', marginLeft: 10 }} thickness={5} size={20} />
          )}
        </Button>
      </div>
    </Paper>
  )
}

export { Download }
